<script>
  import Component from "./component.svelte";
  import widgets from "./widgets.js";

  // widget.svelte

  /** @type {{ def: import("./definition").definition, place?: import("./web").place }} */
  let { def, place = {} } = $props();

  // Definitions that make up this widget, filled in once the widget store
  // resolves. Must be $state so the template re-renders when it updates.
  /** @type {import("./definition").definition[]} */
  let defs = $state([]);

  // Subscribe to the widget store inside an effect so the subscription is
  // cleaned up if the component is destroyed or `def` changes (the original
  // Svelte 4 code never unsubscribed — a latent leak that this fixes).
  $effect(() => {
    if (!def?.r || def.r.val === "") {
      return;
    }

    /** @type {(() => void) | undefined} */
    let unsubscribe;
    widgets.fetch(def.r.val).then((store) => {
      unsubscribe = store.subscribe((list) => {
        defs = Array.isArray(list) ? [...list] : [];
      });
    });

    return () => unsubscribe?.();
  });

  /**
   * Whole pixels a property gives, or 0.
   * @param {string | undefined} value
   * @returns {number}
   */
  const pixelsOf = (value) => (/^\d+$/.test(value ?? "") ? Number(value) : 0);

  // The window width below which the call shows the menu's button in place of its row, or 0 where it always shows its row.
  const below = $derived(pixelsOf(def?.p?.below));

  // The call's own name on the page, which its panel goes by and its button opens.
  const uid = $props.id();
  const panelName = `stenzil-menu-${uid}`;

  // A widget called with styles wears them on its first box in place of its own; a call with a button wears them only from its
  // width up.
  const drawn = $derived.by(() => {
    const styles =
      below > 0
        ? `${def?.p?.styles ?? ""} max-[${below}px]:!hidden`.trim()
        : def?.p?.styles;
    const first = defs.findIndex((d) => d.t === "box");
    if (!styles || first < 0) {
      return defs;
    }
    return defs.map((d, i) =>
      i === first ? { ...d, p: { ...d.p, styles: styles } } : d,
    );
  });

  // A panel lays the menu's entries in a column, each a row a finger can tap, a dropdown's heading too, its entries shown under it.
  const column =
    "flex flex-col items-stretch [&_a]:block [&_a]:px-5 [&_a]:py-3 " +
    "[&_button]:block [&_button]:px-5 [&_button]:py-3 [&_button]:w-full [&_button]:text-left [&>div]:!block " +
    "[&_ul]:!static [&_ul]:!flex [&_ul]:!flex-col [&_ul]:!min-w-0 [&_ul]:!shadow-none " +
    "[&_ul]:!bg-transparent [&_ul]:![color:inherit] [&_ul]:pl-4";

  // A panel given no look of its own is drawn plain.
  const plainLook = "bg-white text-black shadow-md";

  // Below the call's width: its button, drawn as the call's parts in its styles, and the panel the button opens over the page,
  // across the window from just below the button, holding the menu's own row laid out as a column.
  const button = $derived.by(() => {
    const entries = defs.find((d) => d.t === "box");
    const name = `--${panelName}`;
    const opener = {
      t: "button",
      p: {
        styles:
          `${def?.p?.styles ?? ""} min-[${below}px]:!hidden [anchor-name:${name}]`.trim(),
        kind: "button",
        opens: panelName,
        label: "Menu",
      },
      c: def?.c ?? [],
    };
    const panel = {
      t: "box",
      p: {
        id: panelName,
        popover: "auto",
        styles:
          `${def?.p?.panel || plainLook} m-0 p-0 border-0 fixed inset-x-0 bottom-auto w-auto max-w-none h-auto ` +
          `overflow-y-auto hidden [&:popover-open]:block min-[${below}px]:!hidden [position-anchor:${name}] ` +
          `supports-[top:anchor(bottom)]:[top:calc(anchor(bottom)+${pixelsOf(def?.p?.drop)}px)]`,
      },
      c: entries ? [{ ...entries, p: { ...entries.p, styles: column } }] : [],
    };
    return /** @type {import("./definition").definition[]} */ ([opener, panel]);
  });

  // What the call draws: the menu's row, and below its width the button and its panel.
  const parts = $derived(below > 0 ? [...drawn, ...button] : drawn);
</script>

{#each parts as d}
  <Component def={d} {place} />
{/each}
